// src/first.ts
import { castArray } from "lodash-es";
function first(funcs, args, options = {}, index = 0) {
  args = castArray(args);
  if (!funcs[index]) {
    return Promise.reject(new Error(`There is no function on index ${index}`));
  }
  return funcs[index](...args).catch((e) => {
    if (index === funcs.length - 1) {
      throw e;
    }
    return first(funcs, args, options, index + 1);
  });
}

// src/mapInOrder.ts
import Promise2 from "bluebird";
import { compact, each } from "lodash-es";
function mapInOrder(arr, predicate, options = {}) {
  const promises = [];
  each(arr, (item, index) => {
    const promise = predicate(item, index).catch((err) => {
      if (options.failAll) {
        throw err;
      }
      if (options.onError) {
        options.onError(index, err);
      }
      return Promise2.resolve(options.resolveError ? err : null);
    });
    promises.push(promise);
  });
  return Promise2.all(promises).then((results) => {
    if (options.compact) {
      return compact(results);
    }
    return results;
  });
}

// src/retries.ts
import Bluebird from "bluebird";
import { isFunction, isNil, isInteger, chunk } from "lodash-es";
import promiseRetry from "promise-retry";
var defaultOptions = {
  minTimeout: 25,
  retries: 8,
  maxTimeout: Infinity,
  factor: 2
};
function retry(functionToRetry, optionsOverride) {
  return Bluebird.resolve().then(() => {
    const options = Object.assign({}, defaultOptions, optionsOverride);
    const { stopRetryPredicate, retries, minTimeout, maxTimeout, factor } = options;
    if (!isFunction(functionToRetry)) {
      return Bluebird.reject("functionToRetry must be a function");
    }
    if (!isNil(stopRetryPredicate) && !isFunction(stopRetryPredicate)) {
      return Bluebird.reject("stopRetryPredicate must be a function");
    }
    if (isNil(retries) || !isInteger(retries) || retries < 0) {
      return Bluebird.reject(`retries must be an integer >= 0,  got ${retries}`);
    }
    if (isNil(minTimeout) || !isInteger(minTimeout) || minTimeout < 0) {
      return Bluebird.reject("minTimeout must be an integer >= 0");
    }
    let attemptNumber = 0;
    return promiseRetry((retryFunc, internalAttemptNumber) => {
      attemptNumber = internalAttemptNumber;
      return functionToRetry(attemptNumber).catch((error) => {
        if (stopRetryPredicate && stopRetryPredicate(error, attemptNumber)) {
          throw error;
        } else {
          retryFunc(error);
        }
      });
    }, { retries, minTimeout, maxTimeout, factor });
  });
}
function batchOfBatches(handler, list, batchSize, simultaneousBatches) {
  const batches = chunk(list, batchSize);
  const groupsOfBatches = chunk(batches, simultaneousBatches);
  let currentBatchNumber = 0;
  const totalBatches = batches.length;
  return Bluebird.mapSeries(groupsOfBatches, (groupOfBatches) => handler(groupOfBatches, currentBatchNumber, totalBatches).tap(() => currentBatchNumber += batchOfBatches.length));
}

// src/timeout.ts
var rejectOnTimeout = (ms) => new Promise((resolve, reject) => setTimeout(() => reject(new TimeoutError(`Timed out after ${ms / 1e3} seconds!`)), ms));
var withTimeout = (func, ms) => (...args) => {
  return Promise.race([rejectOnTimeout(ms), func(...args)]);
};
var getWrappedMethodWithTimout = withTimeout;
var TimeoutError = class extends Error {
  constructor(message) {
    super(message);
  }
};
export {
  TimeoutError,
  batchOfBatches,
  first,
  getWrappedMethodWithTimout,
  mapInOrder,
  retry,
  withTimeout
};
//# sourceMappingURL=index.js.map
